
import { TripLog } from '@clients/myTrips';
import VehicleSelect from '@components/new-styleguide/form/VehicleSelect.vue';
import { RouterFrom } from '@router/Routes';
import { deepEquals } from '@util/copy';
import storageService from '@util/storageService';
import { LoadState } from '@util/types';
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { RawLocation } from 'vue-router';
import { namespace } from 'vuex-class';

const TriplogModule = namespace('Triplog');
const RouterModule = namespace('Router');

@Component({
  name: 'GlobalVehicleSelect',
  components: {
    VehicleSelect
  }
})
export default class GlobalVehicleSelect extends Vue {
  @TriplogModule.Getter('getVehicles')
  private triplogs: TripLog[];

  @TriplogModule.Getter('getVehiclesStatus')
  private triplogsStatus: LoadState;

  @TriplogModule.Action('fetchVehicles')
  private fetchVehicles;

  @TriplogModule.Action('selectTriplog')
  private selectTriplog;

  @TriplogModule.Getter('getSelectedTriplog')
  private selectedTriplog: TripLog;

  @TriplogModule.Action('fetchLatestTrip')
  private fetchLatestTrip;

  @RouterModule.State('from')
  private from: RouterFrom;

  @Prop({ default: true })
  private allVehiclesOption: boolean;

  @Prop({ default: false })
  private onlySmartConnected: boolean;

  @Prop({ default: false })
  private redirectToSetup: boolean;

  async created() {
    const triplogs: TripLog[] = this.filteredTriplogs;

    if (triplogs.length === 0) {
      this.$router.replace({ name: 'noVehiclesPage' });
      return;
    }

    let triplogId: number = undefined;
    let readTriplogFromQuery = false;
    try {
      if (this.$route.query.vehicle && triplogs.some(vehicle => vehicle.vehicle.id.toString() === this.$route.query.vehicle)) {
        triplogId = parseInt(this.$route.query.vehicle as string);
        readTriplogFromQuery = true;
      } else if (triplogs.length === 1) {
        triplogId = triplogs[0].vehicle.id;
      } else if (storageService.getVehicleId() && this.allVehiclesOption) {
        triplogId = parseInt(storageService.getVehicleId());
      } else if (storageService.getVehicleId() !== "-1" && storageService.getVehicleId() !== null && !this.allVehiclesOption) {
        triplogId = parseInt(storageService.getVehicleId());
      } else {
        const lastTrip = await this.fetchLatestTrip();
        triplogId = lastTrip.vehicleId;
      }
    } catch (err) {
      triplogId = triplogs[0].vehicle.id;
    }

    if (this.onlySmartConnected && !triplogs.some(t => t.vehicle.id === triplogId)) {
      triplogId = triplogs[0].vehicle.id;
    }

    if (this.redirectToSetup) {
      const triplogRequiringSetup = this.filteredTriplogs.find(this.requiresSetup);

      if (triplogRequiringSetup && !this.from.name && (!readTriplogFromQuery || triplogRequiringSetup.vehicle.id === triplogId)) {
        this.handleTriplogInput(triplogRequiringSetup);
        return;
      }
    }

    const selectedTriplog = triplogs.find(v => v.vehicle.id === triplogId);
    this.handleTriplogInput(selectedTriplog);
  }

  get filteredTriplogs() {
    if (this.onlySmartConnected) {
      return this.triplogs.filter(t => t.smartConnected);
    }
    return this.triplogs;
  }

  private requiresSetup(triplog: TripLog) {
    const needsSetup = triplog.vehicle.setupDone === false && triplog.vehicle.deviceId && triplog.smartConnected
    const validFrom = !this.from.name || this.from.name === this.$route.name;
    return needsSetup && validFrom;
  }

  private handleTriplogInput(triplog: TripLog) {
    if (this.selectedTriplog === triplog) {
      return;
    }

    this.selectTriplog({ triplog });

    const query = {
      ...this.$route.query,
      vehicle: triplog ? triplog.vehicle.id.toString() : undefined
    };

    if (triplog) {
      storageService.setVehicleId(triplog.vehicle.id);
    } else {
      storageService.setVehicleId(-1) // -1 represents all vehicles selected option;
    }

    if (!deepEquals(query, this.$route.query)) {
      this.$router.replace({ query });
    }

    this.$emit('input', triplog);

    if (this.redirectToSetup && triplog && this.requiresSetup(triplog)) {
      this.$router.push({
        name: 'vehicleSetup',
        params: { id: triplog.vehicle.id.toString() },
        query: { step: '0', from: this.$route.name }
      })
    }
  }
}
